"use client";

import { Button } from "@/components/ui/button";
import { AlertCircle, Home, RefreshCw } from "lucide-react";
import { useRouter } from "next/navigation";
import React from "react";

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<ErrorFallbackProps>;
}

interface ErrorFallbackProps {
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  resetError: () => void;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console in development
    if (process.env.NODE_ENV === "development") {
      console.error("Error caught by boundary:", error, errorInfo);
    }

    // In production, you could log to an error tracking service
    // Example: Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } });

    this.setState({
      error,
      errorInfo,
    });
  }

  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      const FallbackComponent = this.props.fallback || DefaultErrorFallback;
      return (
        <FallbackComponent
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          resetError={this.resetError}
        />
      );
    }

    return this.props.children;
  }
}

function DefaultErrorFallback({ error, resetError }: ErrorFallbackProps) {
  const router = useRouter();

  const handleGoHome = () => {
    router.push("/");
  };

  return (
    <div className="bg-background flex min-h-svh w-full items-center justify-center px-4 py-12 sm:px-6">
      <div className="mx-auto max-w-lg text-center">
        <div className="mb-6 flex justify-center">
          <div className="bg-destructive/10 text-destructive rounded-full p-4">
            <AlertCircle className="size-8" aria-hidden="true" />
          </div>
        </div>
        <h1 className="mb-2 text-2xl font-semibold tracking-tight sm:text-3xl">
          Something went wrong
        </h1>
        <p className="text-muted-foreground mb-6 text-sm sm:text-base">
          We&apos;re sorry, but something unexpected happened. Please try refreshing the page or
          return to the home page.
        </p>
        {process.env.NODE_ENV === "development" && error && (
          <div className="bg-destructive/10 text-destructive border-destructive/20 mb-6 rounded-lg border p-4 text-left">
            <p className="mb-2 font-mono text-xs font-semibold">Error Details (Dev Only):</p>
            <p className="font-mono text-xs">{error.message}</p>
            {error.stack && (
              <details className="mt-2">
                <summary className="cursor-pointer text-xs font-medium">Stack Trace</summary>
                <pre className="mt-2 max-h-40 overflow-auto text-xs">{error.stack}</pre>
              </details>
            )}
          </div>
        )}
        <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Button onClick={resetError} className="w-full sm:w-auto" variant="default">
            <RefreshCw className="mr-2 size-4" />
            Try again
          </Button>
          <Button onClick={handleGoHome} className="w-full sm:w-auto" variant="outline">
            <Home className="mr-2 size-4" />
            Go to home
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ErrorBoundary;
